// src/layout/builders/ERP/VendorTab.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useERPStore from "../../../store/builders/useERPStore";
import useAuthStore from "../../../store/useAuthStore";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import toast from "react-hot-toast";
import { ITEM_UNIT_MAP, MATERIAL_CATEGORIES } from "../../../lib/materialOptions";
import {
  Plus,
  X,
  Edit2,
  Building2,
  Phone,
  Mail,
  ShoppingCart,
  CheckCircle2,
  Save,
  Trash2,
  PackagePlus,
} from "lucide-react";

const VENDOR_TYPES = ["Brick", "Tiles", "Cement", "Sand", "Steel", "Paint", "Electrical", "Plumbing", "Wood", "Glass", "Other"];
const ALL_UNITS = ["unit", "Bag", "CFT", "Nos", "kg", "Feet", "Meter", "Liters", "sq.ft", "Sheets", "m³"];
const ALL_UNIT_SET = new Set([...Object.values(ITEM_UNIT_MAP), ...ALL_UNITS]);

const emptyVendorForm = { name: "", type: "Brick", contact: "", email: "", address: "" };

const newRow = () => ({
  _id: Math.random().toString(36).slice(2),
  item_name: "",
  quantity: "",
  unit: "unit",
  unit_rate: "",
  paid_amount: "",
  purchase_date: new Date().toISOString().split("T")[0],
  notes: "",
  project_id: "",
  _otherMaterial: false,
  _customUnit: false,
});

// Single editable row for the inline purchase table
const PurchaseRow = ({ row, idx, onChange, onRemove, canRemove, projects }) => {
  const unitList = row.item_name && ITEM_UNIT_MAP[row.item_name]
    ? [ITEM_UNIT_MAP[row.item_name], ...Array.from(ALL_UNIT_SET).filter(u => u !== ITEM_UNIT_MAP[row.item_name])]
    : Array.from(ALL_UNIT_SET);

  const total = (parseFloat(row.quantity) || 0) * (parseFloat(row.unit_rate) || 0);

  const handleItemChange = (val) => {
    if (val === "__other__") {
      onChange(idx, { item_name: "", _otherMaterial: true });
      return;
    }
    const suggestedUnit = ITEM_UNIT_MAP[val];
    onChange(idx, { item_name: val, _otherMaterial: false, ...(suggestedUnit ? { unit: suggestedUnit, _customUnit: false } : {}) });
  };

  const handleUnitChange = (val) => {
    if (val === "__custom__") {
      onChange(idx, { unit: "", _customUnit: true });
      return;
    }
    onChange(idx, { unit: val, _customUnit: false });
  };

  return (
    <tr className="group border-b border-gray-100 hover:bg-blue-50/20 transition-colors">
      {/* # */}
      <td className="px-3 py-2 text-center text-[11px] font-bold text-gray-400 w-8">{idx + 1}</td>

      {/* Item Name */}
      <td className="px-2 py-2 min-w-[160px]">
        {!row._otherMaterial ? (
          <select
            id={idx === 0 ? "first-purchase-row-select" : undefined}
            value={row.item_name}
            onChange={e => handleItemChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 bg-white text-gray-700 font-medium transition-colors"
          >
            <option value="" disabled>Select...</option>
            {row.item_name && !ITEM_UNIT_MAP[row.item_name] && row.item_name !== "__other__" && (
              <option value={row.item_name}>{row.item_name} (Custom)</option>
            )}
            {MATERIAL_CATEGORIES.map(cat => (
              <optgroup key={cat.category} label={cat.category}>
                {cat.items.map(item => <option key={item} value={item}>{item}</option>)}
              </optgroup>
            ))}
            <option value="__other__" className="font-bold text-blue-600">+ Custom Material</option>
          </select>
        ) : (
          <div className="flex gap-1">
            <input
              id={idx === 0 ? "first-purchase-row-select" : undefined}
              autoFocus
              placeholder="Type material..."
              value={row.item_name}
              onChange={e => onChange(idx, { item_name: e.target.value })}
              className="w-full border border-blue-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-500 font-medium"
            />
            <button type="button" onClick={() => onChange(idx, { item_name: "", _otherMaterial: false })}
              className="p-1 text-gray-400 hover:text-red-500 rounded shrink-0"><X size={12} /></button>
          </div>
        )}
      </td>

      {/* Quantity */}
      <td className="px-2 py-2 w-20">
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="0"
          value={row.quantity}
          onChange={e => onChange(idx, { quantity: e.target.value })}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 text-right font-medium"
        />
      </td>

      {/* Unit */}
      <td className="px-2 py-2 w-24">
        {!row._customUnit ? (
          <select
            value={row.unit}
            onChange={e => handleUnitChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 bg-white"
          >
            {unitList.map(u => <option key={u} value={u}>{u}</option>)}
            <option value="__custom__" className="font-bold text-blue-600">+ Custom</option>
          </select>
        ) : (
          <div className="flex gap-1">
            <input
              autoFocus
              placeholder="Unit..."
              value={row.unit}
              onChange={e => onChange(idx, { unit: e.target.value })}
              className="w-full border border-blue-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-500"
            />
            <button type="button" onClick={() => onChange(idx, { unit: "unit", _customUnit: false })}
              className="p-1 text-gray-400 hover:text-red-500 rounded shrink-0"><X size={12} /></button>
          </div>
        )}
      </td>

      {/* Rate */}
      <td className="px-2 py-2 w-24">
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="0.00"
          value={row.unit_rate}
          onChange={e => onChange(idx, { unit_rate: e.target.value })}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 text-right font-medium"
        />
      </td>

      {/* Total (auto) */}
      <td className="px-2 py-2 w-24 text-right">
        <span className="font-black text-blue-700 text-xs">
          ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </td>

      {/* Date */}
      <td className="px-2 py-2 w-32">
        <input
          type="date"
          value={row.purchase_date}
          onChange={e => onChange(idx, { purchase_date: e.target.value })}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400"
        />
      </td>

      {/* Project */}
      <td className="px-2 py-2 w-28">
        <select
          value={row.project_id}
          onChange={e => onChange(idx, { project_id: e.target.value })}
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-blue-400 bg-white"
        >
          <option value="">Godown</option>
          {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.project_name}</option>)}
        </select>
      </td>

      {/* Remove */}
      <td className="px-2 py-2 w-8 text-center">
        {canRemove && (
          <button type="button" onClick={() => onRemove(idx)}
            className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100">
            <Trash2 size={13} />
          </button>
        )}
      </td>
    </tr>
  );
};


const VendorTab = () => {
  const { vendors, purchases, isLoading, fetchVendors, createVendor, updateVendor, fetchVendorPurchases, createPurchase, updatePurchase, vendorPayments, fetchVendorPayments, createVendorPayment, vendorOutstandingBalances, fetchVendorOutstandingBalances } = useERPStore();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [projects, setProjects] = useState([]);

  // Vendor modal
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState(emptyVendorForm);
  const [saving, setSaving] = useState(false);

  // Vendor detail view
  const [selectedVendorForDetails, setSelectedVendorForDetails] = useState(null);

  // Multi-row inline purchase form
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [purchaseRows, setPurchaseRows] = useState([newRow()]);
  const [editingPurchase, setEditingPurchase] = useState(null); // single-edit mode
  const [editRow, setEditRow] = useState(null); // the row being edited
  const [savingPurchase, setSavingPurchase] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [paidGrandTotal, setPaidGrandTotal] = useState("");
  const purchaseFormRef = useRef(null);

  // Vendor state & filters
  const [historyDateFrom, setHistoryDateFrom] = useState("");
  const [historyDateTo, setHistoryDateTo] = useState("");
  const [activePayPurchaseId, setActivePayPurchaseId] = useState(null);
  const [selectedHistoryPurchase, setSelectedHistoryPurchase] = useState(null);
  const [payAmountInput, setPayAmountInput] = useState("");
  const [submittingPay, setSubmittingPay] = useState(false);
  const [vendorPayAmount, setVendorPayAmount] = useState("");
  const [vendorPayNotes, setVendorPayNotes] = useState("");

  useEffect(() => {
    fetchVendorOutstandingBalances();
  }, [fetchVendorOutstandingBalances]);

  const handleVendorPayment = async (e) => {
    e.preventDefault();
    const payVal = parseFloat(vendorPayAmount);
    if (!payVal || payVal <= 0) return toast.error("Invalid amount");
    if (payVal > totalBalance) return toast.error(`Payment cannot exceed outstanding balance of ₹${totalBalance.toLocaleString()}`);
    setSubmittingPay(true);
    const ok = await createVendorPayment(selectedVendorForDetails.id, {
      amount: payVal,
      notes: vendorPayNotes,
      payment_date: new Date().toISOString().split("T")[0]
    });
    setSubmittingPay(false);
    if (ok) {
      setVendorPayAmount("");
      setVendorPayNotes("");
    }
  };

  useEffect(() => {
    fetchVendors();
    if (user?.id) {
      axiosInstancev1.get(`/projects?builderId=${user.id}&status=active&limit=1000&page=1`)
        .then(r => setProjects(r.data?.projects || [])).catch(() => { });
    }
  }, [fetchVendors, user?.id]);

  const openVendorModal = (vendor = null) => {
    setEditingVendor(vendor);
    setVendorForm(vendor
      ? { name: vendor.name, type: vendor.type, contact: vendor.contact || "", email: vendor.email || "", address: vendor.address || "", custom_type: "" }
      : emptyVendorForm);
    setShowVendorModal(true);
  };

  const handleVendorSave = async (e) => {
    e.preventDefault();
    if (vendorForm.contact && vendorForm.contact.length !== 10) {
      toast.error("Contact number must be exactly 10 digits");
      return;
    }
    setSaving(true);
    let ok;
    const payload = {
      ...vendorForm,
      type: vendorForm.type === "Other" ? (vendorForm.custom_type || "Other") : vendorForm.type
    };
    delete payload.custom_type;

    if (editingVendor) ok = await updateVendor(editingVendor.id, payload);
    else ok = await createVendor(payload);
    setSaving(false);
    if (ok) setShowVendorModal(false);
  };

  const openVendorDetails = (vendor) => {
    setSelectedVendorForDetails(vendor);
    setShowPurchaseForm(false);
    setEditingPurchase(null);
    setEditRow(null);
    setSavedCount(0);
    setHistoryDateFrom("");
    setHistoryDateTo("");
    setActivePayPurchaseId(null);
    fetchVendorPurchases(vendor.id);
    fetchVendorPayments(vendor.id);
  };

  const openNewPurchaseForm = () => {
    if (selectedVendorForDetails) {
      setPurchaseRows([newRow(), newRow(), newRow(), newRow(), newRow()]);
      setEditingPurchase(null);
      setEditRow(null);
      setShowPurchaseForm(true);
      setTimeout(() => {
        const el = document.getElementById("first-purchase-row-select");
        if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
      }, 80);
    }
  };

  const openEditPurchaseForm = (purchase) => {
    if (selectedVendorForDetails) {
      setPurchaseRows([{
        _id: Math.random().toString(36).slice(2),
        item_name: purchase.item_name || "",
        quantity: purchase.quantity || "",
        unit: purchase.unit || "unit",
        unit_rate: purchase.unit_rate || "",
        purchase_date: purchase.purchase_date?.split("T")[0] || "",
        notes: purchase.notes || "",
        project_id: purchase.project_id || "",
        _otherMaterial: purchase.item_name && !ITEM_UNIT_MAP[purchase.item_name],
        _customUnit: purchase.unit && !ALL_UNIT_SET.has(purchase.unit),
      }]);
      setEditingPurchase(purchase);
      setShowPurchaseForm(true);
    }
  };

  const closePurchaseForm = () => {
    setShowPurchaseForm(false);
    setEditingPurchase(null);
    setEditRow(null);
    setPurchaseRows([newRow()]);
    setPaidGrandTotal("");
  };

  const handlePurchaseSave = async () => {
    const validRows = purchaseRows.filter(r => r.item_name && r.item_name.trim() !== "");
    if (validRows.length === 0) {
      toast.error("Please fill in at least one item.");
      return;
    }

    setSavingPurchase(true);
    let allOk = true;
    let saved = 0;

    for (const row of validRows) {
      const totalAmt = (parseFloat(row.quantity) || 0) * (parseFloat(row.unit_rate) || 0);
      const payload = {
        item_name: row.item_name,
        quantity: row.quantity,
        unit: row.unit,
        unit_rate: row.unit_rate,
        total_amount: totalAmt.toString(),
        paid_amount: "0",
        outstanding: totalAmt.toString(),
        purchase_date: row.purchase_date,
        project_id: row.project_id,
        notes: row.notes,
        vendor_id: selectedVendorForDetails.id,
      };

      let ok;
      if (editingPurchase) {
        ok = await updatePurchase(editingPurchase.id, payload);
      } else {
        ok = await createPurchase(payload);
      }
      
      if (ok) {
        saved++;
      } else {
        allOk = false;
      }
    }

    setSavingPurchase(false);
    if (allOk) {
      setSavedCount(saved);
      toast.success("Purchases saved successfully!");
      fetchVendorPurchases(selectedVendorForDetails.id);
      fetchVendorOutstandingBalances();
      closePurchaseForm();
    } else {
      toast.error("Some purchases failed to save.");
    }
  };

  const vendorPurchases = purchases;
  const totalPurchaseVal = vendorPurchases.reduce((s, p) => s + parseFloat(p.total_amount || 0), 0);
  const vendorObj = vendorOutstandingBalances.find(v => v.vendor_id === selectedVendorForDetails?.id);
  const totalBalance = vendorObj ? parseFloat(vendorObj.total_outstanding || 0) : 0;
  const totalPaid = totalPurchaseVal - totalBalance;

  const filteredVendorPurchases = vendorPurchases.filter(p => {
    if (!p.purchase_date) return true;
    const pDate = new Date(p.purchase_date);
    pDate.setHours(0, 0, 0, 0);

    if (historyDateFrom) {
      const fromD = new Date(historyDateFrom);
      fromD.setHours(0, 0, 0, 0);
      if (pDate < fromD) return false;
    }
    if (historyDateTo) {
      const toD = new Date(historyDateTo);
      toD.setHours(0, 0, 0, 0);
      if (pDate > toD) return false;
    }
    return true;
  });



  return (
    <div className="space-y-6">
      {/* Header - Hidden when viewing vendor ledger/details */}
      {!selectedVendorForDetails && (
        <div className="flex items-center justify-between flex-wrap gap-4 bg-white p-4 rounded-xl border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100">
              <Building2 size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Vendor Management</h2>
              <p className="text-xs text-gray-500 font-medium">{vendors.length} Strategic Partners</p>
            </div>
          </div>
          <button onClick={() => openVendorModal()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition shadow-sm">
            <Plus size={16} /> Add Vendor
          </button>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-100 border-t-blue-600"></div>
          <p className="text-sm font-medium text-slate-500">Synchronizing vendor accounts...</p>
        </div>
      ) : vendors.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-16 text-center flex flex-col items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center text-slate-200 mb-2">
            <Building2 size={40} />
          </div>
          <div>
            <p className="text-slate-800 font-bold text-lg">No Vendors Onboarded</p>
            <p className="text-slate-500 text-sm max-w-xs mx-auto mt-1 font-medium">Record and manage your suppliers, material purchases, and outstanding payments here.</p>
          </div>
          <button onClick={() => openVendorModal()} className="mt-2 text-blue-600 font-bold text-sm hover:underline">Onboard first vendor</button>
        </div>
      ) : selectedVendorForDetails ? (
        /* ─── VENDOR DETAIL VIEW ─── */
        <div className="space-y-4">
          {/* Top bar */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between p-4 border-b bg-gray-50 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setSelectedVendorForDetails(null); setShowPurchaseForm(false); }}
                  className="p-1 flex items-center justify-center text-gray-500 hover:text-blue-600 bg-white border border-gray-200 rounded shadow-sm mr-2 w-7 h-7" title="Back">
                  <X size={16} />
                </button>
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-500 text-xs border border-gray-200">
                  {selectedVendorForDetails.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-sm ml-1">{selectedVendorForDetails.name}</h3>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider ml-1">{selectedVendorForDetails.type}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {savedCount > 0 && (
                  <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-3 py-1 text-[11px] font-bold">
                    <CheckCircle2 size={12} />
                    {savedCount} {savedCount === 1 ? "entry" : "entries"} saved!
                  </div>
                )}
                <button
                  onClick={showPurchaseForm ? closePurchaseForm : openNewPurchaseForm}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[11px] font-bold transition shadow-sm ${showPurchaseForm ? "bg-gray-100 text-gray-600 hover:bg-gray-200" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
                  {showPurchaseForm ? <><X size={14} /> Close</> : <><Plus size={14} /> New Purchase</>}
                </button>
              </div>
            </div>

            {/* Summary Cards - Total Amount Purchased & Total Remaining Balance */}
            <div className="p-4 bg-gray-50/50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center border border-blue-100 text-blue-600 font-bold"><ShoppingCart size={22} /></div>
                  <div>
                    <span className="text-[10px] uppercase font-extrabold text-gray-400 block mb-0.5 tracking-wider">Total Amount Purchased</span>
                    <span className="text-lg font-black text-gray-800">₹{totalPurchaseVal.toLocaleString()}</span>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center border border-red-100 text-red-500 font-black text-lg">₹</div>
                  <div>
                    <span className="text-[10px] uppercase font-extrabold text-red-500 block mb-0.5 tracking-wider">Total Remaining Balance</span>
                    <span className="text-lg font-black text-red-700">₹{totalBalance.toLocaleString()}</span>
                  </div>
                </div>
              </div>

            {/* Purchase Form Inline */}
            {showPurchaseForm && (
              <div className="bg-blue-50/50 p-4 border-b border-blue-100 mb-4 rounded-xl border shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-blue-800">
                    <PackagePlus size={18} />
                    <h3 className="font-bold text-sm">{editingPurchase ? "Edit Purchase Entry" : "Add Purchase Entries"}</h3>
                    {!editingPurchase && <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full ml-2">{purchaseRows.length} rows</span>}
                  </div>
                </div>
                
                <div className="overflow-x-auto bg-white rounded-xl border border-blue-200 shadow-sm mb-3">
                  <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                      <tr className="border-b-2 border-gray-100 bg-gray-50">
                        <th className="px-3 py-2 text-center text-[10px] font-extrabold text-gray-500 uppercase tracking-wider w-8">#</th>
                        <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">ITEM / MATERIAL *</th>
                        <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider text-center w-20">QTY</th>
                        <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider w-24">UNIT</th>
                        <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider text-right w-24">RATE (₹)</th>
                        <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider text-right w-24">TOTAL</th>
                        <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider w-32">DATE</th>
                        <th className="px-2 py-2 text-[10px] font-extrabold text-gray-500 uppercase tracking-wider w-28">PROJECT</th>
                        <th className="px-2 py-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseRows.map((row, idx) => (
                        <PurchaseRow
                          key={row._id}
                          row={row}
                          idx={idx}
                          onChange={(i, updates) => {
                            const updated = [...purchaseRows];
                            updated[i] = { ...updated[i], ...updates };
                            setPurchaseRows(updated);
                          }}
                          onRemove={(i) => {
                            setPurchaseRows(purchaseRows.filter((_, index) => index !== i));
                          }}
                          canRemove={purchaseRows.length > 1}
                          projects={projects}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    {!editingPurchase && (
                      <button
                        type="button"
                        onClick={() => setPurchaseRows([...purchaseRows, newRow()])}
                        className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 text-[11px] font-bold border border-blue-200 border-dashed rounded-lg px-4 py-2 hover:bg-white transition-colors bg-blue-50/50"
                      >
                        <Plus size={14} /> Add Another Row
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={closePurchaseForm}
                      className="px-5 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePurchaseSave}
                      disabled={savingPurchase}
                      className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
                    >
                      <Save size={16} />
                      {savingPurchase ? "Saving..." : "Save Purchases"}
                    </button>
                  </div>
                </div>
              </div>
            )}

              {/* Vendor Purchase Items */}
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3 bg-white p-3 rounded-xl border border-gray-200">
                <div className="font-extrabold text-gray-800 text-xs uppercase tracking-wider">Purchased Items</div>
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className="text-gray-500 font-medium text-[11px]">Filter Date:</span>
                  <input
                    type="date"
                    value={historyDateFrom}
                    onChange={e => setHistoryDateFrom(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-blue-500 bg-white"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={historyDateTo}
                    onChange={e => setHistoryDateTo(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-blue-500 bg-white"
                  />
                  {(historyDateFrom || historyDateTo) && (
                    <button
                      onClick={() => { setHistoryDateFrom(""); setHistoryDateTo(""); }}
                      className="text-[11px] text-red-500 hover:underline font-bold ml-1"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Purchase History Table */}
              {filteredVendorPurchases.length === 0 ? (
                <div className="text-center py-10 bg-white rounded-xl border border-dashed border-gray-200 shadow-sm mb-6">
                  <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 mx-auto mb-3"><ShoppingCart size={24} /></div>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">No transaction records found</p>
                  <p className="text-[10px] text-gray-400 mt-1">Click "New Purchase" to record billing details</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm mb-6">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                        <th className="text-left px-4 py-3">Material</th>
                        <th className="text-center px-4 py-3">Qty</th>
                        <th className="text-right px-4 py-3">Rate</th>
                        <th className="text-right px-4 py-3">Total Amount</th>
                        <th className="text-left px-4 py-3">Date</th>
                        <th className="text-left px-4 py-3">Project</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredVendorPurchases.map((p) => {
                        const totalAmt = parseFloat(p.total_amount || 0);
                        const paidAmt = parseFloat(p.paid_amount || 0);
                        const remBalance = parseFloat(p.balance_amount !== undefined ? p.balance_amount : (totalAmt - paidAmt));
                        const isPaying = activePayPurchaseId === p.id;

                        return (
                          <React.Fragment key={p.id}>
                            <tr className="hover:bg-blue-50/30 transition-colors">
                              <td className="px-4 py-3">
                                <div className="font-bold text-gray-800">{p.item_name}</div>
                                {p.notes && (
                                  <div
                                    className="text-blue-500 font-semibold text-[10px] truncate max-w-[150px] cursor-pointer hover:text-blue-700 hover:underline mt-0.5"
                                    onClick={() => setSelectedHistoryPurchase(p)}
                                    title="Click to view full history"
                                  >
                                    {p.notes.split('|').length > 1 ? `View History (${p.notes.split('|').length - 1} payments)` : p.notes}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-bold text-[11px]">{p.quantity} {p.unit}</span>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-gray-500">₹{parseFloat(p.unit_rate).toLocaleString()}</td>
                              <td className="px-4 py-3 text-right font-black text-gray-800">₹{totalAmt.toLocaleString()}</td>
                              <td className="px-4 py-3 text-gray-600 font-bold text-[10px] whitespace-nowrap">
                                {new Date(p.purchase_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </td>
                              <td className="px-4 py-3 font-semibold text-gray-600 text-[11px]">
                                {p.project_name || <span className="text-gray-300 font-normal">Godown</span>}
                              </td>

                              <td className="px-4 py-3 text-right">
                                <button onClick={() => openEditPurchaseForm(p)} className="p-1.5 text-gray-400 hover:text-blue-600 border border-transparent hover:border-blue-200 rounded hover:bg-blue-50 transition-colors">
                                  <Edit2 size={14} />
                                </button>
                              </td>
                            </tr>


                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* VENDOR GLOBAL PAYMENT SECTION */}
              <div className="bg-white p-4 rounded-xl border border-blue-200 shadow-sm mb-4">
                <h4 className="font-bold text-gray-800 text-sm mb-3">Make Payment to Vendor</h4>
                <form onSubmit={handleVendorPayment} className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Amount (Max: ₹{totalBalance.toLocaleString()})</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-gray-500">₹</span>
                      <input
                        type="number" step="0.01" min="0.01" max={totalBalance} required
                        value={vendorPayAmount} onChange={e => setVendorPayAmount(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm outline-none focus:border-blue-500 font-bold text-gray-700"
                      />
                    </div>
                  </div>
                  <div className="flex-2 min-w-[300px]">
                    <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Payment Reference / Notes</label>
                    <input
                      type="text" value={vendorPayNotes} onChange={e => setVendorPayNotes(e.target.value)}
                      placeholder="e.g. Bank Transfer Ref 123456"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </div>
                  <button type="submit" disabled={submittingPay || totalBalance <= 0}
                    className="px-6 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition disabled:opacity-50">
                    {submittingPay ? "Processing..." : "Pay Vendor"}
                  </button>
                </form>
              </div>

              {/* PAYMENT HISTORY TABLE */}
              <div className="mb-6">
                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wider mb-2">Payment History</h4>
                {vendorPayments.length === 0 ? (
                  <div className="text-center py-6 bg-white rounded-xl border border-dashed border-gray-200 shadow-sm text-gray-400 text-xs font-medium">
                    No payments recorded yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm max-h-60 overflow-y-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead className="sticky top-0 bg-gray-50 z-10">
                        <tr className="border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                          <th className="text-left px-4 py-2">Date</th>
                          <th className="text-right px-4 py-2">Amount Paid</th>
                          <th className="text-left px-4 py-2">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {vendorPayments.map(pay => (
                          <tr key={pay.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-semibold text-gray-600">
                              {new Date(pay.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-2 text-right font-black text-emerald-600">
                              ₹{parseFloat(pay.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-2 text-gray-500">{pay.notes || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ─── VENDOR LIST ─── */
        <div className="space-y-4">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-lg shrink-0 border border-gray-200">
                    {vendor.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-bold text-gray-800 text-base truncate">{vendor.name}</p>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-bold uppercase tracking-wider border border-gray-200">{vendor.type}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500 text-[11px] font-medium">
                      {vendor.contact && <div className="flex items-center gap-1"><Phone size={10} /> {vendor.contact}</div>}
                      {vendor.email && <div className="flex items-center gap-1"><Mail size={10} /> {vendor.email}</div>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 border-t sm:border-t-0 pt-3 sm:pt-0">
                  <button onClick={() => openVendorDetails(vendor)}
                    className="flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1.5 rounded-lg text-[11px] font-bold hover:bg-blue-100 transition shadow-sm">
                    <ShoppingCart size={12} /> View Ledger & Purchases
                  </button>
                  <button onClick={() => openVendorModal(vendor)} className="p-1.5 text-gray-400 hover:text-blue-600 bg-white border border-gray-200 rounded-lg shadow-sm ml-1">
                    <Edit2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Vendor Add/Edit Modal */}
      {showVendorModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-800">{editingVendor ? "Edit Vendor" : "Add Vendor"}</h3>
              <button onClick={() => setShowVendorModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleVendorSave} className="p-4 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Company Name *</label>
                <input required value={vendorForm.name} onChange={e => setVendorForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Type *</label>
                  <select required value={vendorForm.type} onChange={e => setVendorForm(p => ({ ...p, type: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white">
                    {VENDOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Contact (10 Digits)</label>
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="10-digit number"
                    value={vendorForm.contact}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setVendorForm(p => ({ ...p, contact: val }));
                    }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>
              {vendorForm.type === "Other" && (
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Specify Vendor Type *</label>
                  <input
                    required
                    placeholder="e.g. Hardware, Ready-mix, Glass, etc."
                    value={vendorForm.custom_type || ""}
                    onChange={e => setVendorForm(p => ({ ...p, custom_type: e.target.value }))}
                    className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 font-medium"
                  />
                </div>
              )}
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Email</label>
                <input type="email" value={vendorForm.email} onChange={e => setVendorForm(p => ({ ...p, email: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Address</label>
                <textarea rows={2} value={vendorForm.address} onChange={e => setVendorForm(p => ({ ...p, address: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowVendorModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History Modal */}
      {selectedHistoryPurchase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div>
                <h3 className="font-bold text-gray-800">History: {selectedHistoryPurchase.item_name}</h3>
                <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                  Total Purchase: ₹{parseFloat(selectedHistoryPurchase.total_amount).toLocaleString()}
                </p>
              </div>
              <button onClick={() => setSelectedHistoryPurchase(null)} className="p-1 text-gray-400 hover:text-red-500 rounded bg-white border shadow-sm">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {selectedHistoryPurchase.notes ? (
                <div className="space-y-2">
                  {selectedHistoryPurchase.notes.split('|').map((note, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm text-gray-700">
                      {note.trim()}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No history or notes available.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorTab;
